import React, { useCallback, useEffect, useState } from "react";
import api from "@/lib/api";
import { num, formatDate } from "@/lib/format";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { ArrowDownToLine, RefreshCcw, AlertTriangle } from "lucide-react";
import { toast } from "sonner";
import { ProdStatusBadge } from "./Production";

export default function ProductionMaterials() {
  const [rows, setRows] = useState([]);
  const [issueTarget, setIssueTarget] = useState(null); // detail doc utk pengambilan
  const [issueItems, setIssueItems] = useState([]);
  const [reconTarget, setReconTarget] = useState(null);
  const [reconItems, setReconItems] = useState([]);

  const load = useCallback(async () => {
    const r = await api.get("/productions");
    setRows(r.data.filter((x) => ["PLANNED", "MATERIAL_ISSUED", "IN_PROGRESS", "WAITING_CHECK"].includes(x.status)));
  }, []);
  useEffect(() => { load().catch((e) => console.error(e)); }, [load]);

  const openIssue = async (id) => {
    const d = (await api.get(`/productions/${id}`)).data;
    setIssueTarget(d);
    setIssueItems(d.materials.map((m) => ({ product_id: m.product_id, sku: m.sku, name: m.name, unit: m.unit, theoretical: m.theoretical_qty, stock: m.warehouse_stock, qty: m.theoretical_qty, material_type: m.material_type })));
  };

  const doIssue = async () => {
    try {
      await api.post(`/productions/${issueTarget.production_id}/issue-materials`, { items: issueItems.map(({ product_id, qty }) => ({ product_id, qty: Number(qty) })) });
      toast.success("Bahan diserahkan ke produksi (gudang berkurang)");
      setIssueTarget(null); await load();
    } catch (e) { toast.error(e.response?.data?.detail || "Gagal"); }
  };

  const openRecon = async (id) => {
    const d = (await api.get(`/productions/${id}`)).data;
    setReconTarget(d);
    setReconItems(d.materials.map((m) => ({ product_id: m.product_id, sku: m.sku, name: m.name, unit: m.unit, issued: m.issued_qty, used: m.issued_qty, leftover: 0, waste: 0, waste_reason: "", material_type: m.material_type })));
  };

  const doRecon = async () => {
    try {
      await api.post(`/productions/${reconTarget.production_id}/reconcile-materials`, {
        items: reconItems.map((r) => ({ product_id: r.product_id, used_qty: Number(r.used), leftover_qty: Number(r.leftover), waste_qty: Number(r.waste), waste_reason: r.waste_reason })),
      });
      toast.success("Bahan tereksiliasi — sisa kembali ke gudang, waste menunggu approval");
      setReconTarget(null); await load();
    } catch (e) { toast.error(e.response?.data?.detail || "Gagal"); }
  };

  return (
    <div data-testid="page-production-materials" className="space-y-6">
      <div>
        <div className="text-xs font-mono uppercase tracking-widest text-slate-500 mb-2">Produksi</div>
        <h1 className="font-display text-3xl font-bold text-slate-900">Pengambilan & Rekonsiliasi Bahan</h1>
        <p className="text-slate-500 text-sm mt-1">Bahan diambil BERSAMA per produksi (1 transaksi gudang). Sisa/waste direkonsiliasi: diambil = terpakai + sisa + waste.</p>
      </div>

      <Card>
        <table className="w-full text-sm" data-testid="table-materials-queue">
          <thead><tr className="bg-slate-50 text-left text-xs font-mono uppercase tracking-wider text-slate-500">
            <th className="py-2 px-4">No. Produksi</th><th>Tanggal</th><th>Produk</th><th className="text-right">Target</th><th>Status</th><th></th>
          </tr></thead>
          <tbody>
            {rows.map((r) => (
              <tr key={r.production_id} className="border-b">
                <td className="py-2 px-4 font-mono text-xs">{r.prod_no}</td>
                <td>{formatDate(r.date)}</td>
                <td>{r.product_name}</td>
                <td className="text-right font-mono">{num(r.target_dus)} dus</td>
                <td><ProdStatusBadge status={r.status} /></td>
                <td className="text-right whitespace-nowrap">
                  {r.status === "PLANNED" && <Button size="sm" className="bg-slate-900 hover:bg-slate-800" onClick={() => openIssue(r.production_id)} data-testid={`issue-${r.prod_no}`}><ArrowDownToLine className="h-3.5 w-3.5 mr-1" />Ambil Bahan</Button>}
                  {["IN_PROGRESS", "WAITING_CHECK"].includes(r.status) && <Button size="sm" variant="outline" onClick={() => openRecon(r.production_id)} data-testid={`recon-${r.prod_no}`}><RefreshCcw className="h-3.5 w-3.5 mr-1" />Rekonsiliasi Bahan</Button>}
                </td>
              </tr>
            ))}
            {rows.length === 0 && <tr><td colSpan={6} className="py-8 text-center text-slate-400">Tidak ada produksi yang menunggu aksi bahan</td></tr>}
          </tbody>
        </table>
      </Card>

      {/* Dialog Ambil Bahan */}
      <Dialog open={!!issueTarget} onOpenChange={(o) => !o && setIssueTarget(null)}>
        <DialogContent className="max-w-3xl">
          <DialogHeader><DialogTitle>Ambil Bahan — {issueTarget?.prod_no}</DialogTitle></DialogHeader>
          {issueTarget && (
            <div data-testid="issue-dialog">
              <table className="w-full text-sm">
                <thead><tr className="text-left text-xs text-slate-500 border-b">
                  <th className="py-1">Bahan</th><th className="text-right">Kebutuhan Teoritis</th><th className="text-right">Stok Gudang</th><th className="text-right">Diambil</th>
                </tr></thead>
                <tbody>
                  {issueItems.map((it, i) => {
                    const kurang = Number(it.qty) > it.stock;
                    return (
                      <tr key={it.product_id} className="border-b border-slate-100">
                        <td className="py-2">{it.sku} · {it.name} {it.material_type === "consumable" && <Badge className="bg-amber-100 text-amber-800 font-normal ml-1">Consumable</Badge>}</td>
                        <td className="text-right font-mono">{num(it.theoretical)} {it.unit}</td>
                        <td className={`text-right font-mono ${kurang ? "text-rose-700 font-bold" : ""}`} data-testid={`issue-stock-${it.sku}`}>{num(it.stock)}</td>
                        <td className="text-right">
                          <Input type="number" className={`w-24 ml-auto ${kurang ? "border-rose-400" : ""}`} value={it.qty}
                            onChange={(e) => { const n = [...issueItems]; n[i].qty = e.target.value; setIssueItems(n); }} data-testid={`issue-qty-${it.sku}`} />
                          {kurang && <div className="text-[10px] text-rose-600 flex items-center justify-end gap-1 mt-0.5"><AlertTriangle className="h-3 w-3" />Stok kurang!</div>}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
              <p className="text-xs text-slate-500 mt-2">Gudang berkurang, bahan menjadi tanggung jawab produksi sampai dipakai/dikembalikan/di-waste.</p>
            </div>
          )}
          <DialogFooter><Button className="bg-slate-900 hover:bg-slate-800" onClick={doIssue} data-testid="btn-confirm-issue">Serahkan ke Produksi</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Dialog Rekonsiliasi */}
      <Dialog open={!!reconTarget} onOpenChange={(o) => !o && setReconTarget(null)}>
        <DialogContent className="max-w-4xl">
          <DialogHeader><DialogTitle>Rekonsiliasi Bahan — {reconTarget?.prod_no}</DialogTitle></DialogHeader>
          {reconTarget && (
            <div data-testid="recon-dialog">
              <table className="w-full text-sm">
                <thead><tr className="text-left text-xs text-slate-500 border-b">
                  <th className="py-1">Bahan</th><th className="text-right">Diambil</th><th className="text-right">Terpakai</th>
                  <th className="text-right">Sisa (kembali gudang)</th><th className="text-right">Waste</th><th>Alasan Waste</th><th className="text-right">Cek</th>
                </tr></thead>
                <tbody>
                  {reconItems.map((it, i) => {
                    const sum = Number(it.used) + Number(it.leftover) + Number(it.waste);
                    const balanced = sum === it.issued;
                    const set = (patch) => { const n = [...reconItems]; n[i] = { ...n[i], ...patch }; setReconItems(n); };
                    return (
                      <tr key={it.product_id} className="border-b border-slate-100">
                        <td className="py-2">{it.sku} · {it.name}</td>
                        <td className="text-right font-mono">{num(it.issued)} {it.unit}</td>
                        <td className="text-right"><Input type="number" className="w-20 ml-auto" value={it.used} onChange={(e) => set({ used: e.target.value })} data-testid={`recon-used-${it.sku}`} /></td>
                        <td className="text-right"><Input type="number" className="w-20 ml-auto" value={it.leftover} onChange={(e) => set({ leftover: e.target.value })} data-testid={`recon-leftover-${it.sku}`} /></td>
                        <td className="text-right"><Input type="number" className="w-20 ml-auto" value={it.waste} onChange={(e) => set({ waste: e.target.value })} data-testid={`recon-waste-${it.sku}`} /></td>
                        <td>{Number(it.waste) > 0 && <Input placeholder="mis. bahan rusak" value={it.waste_reason} onChange={(e) => set({ waste_reason: e.target.value })} data-testid={`recon-waste-reason-${it.sku}`} />}</td>
                        <td className="text-right" data-testid={`recon-check-${it.sku}`}>
                          {balanced ? <Badge className="bg-emerald-100 text-emerald-800 font-normal">SEIMBANG</Badge> : <Badge className="bg-rose-100 text-rose-800 font-normal">SELISIH {num(it.issued - sum)}</Badge>}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
              <p className="text-xs text-slate-500 mt-2">Sisa → movement RETURN_FROM_PRODUCTION ke gudang. Waste → status PENDING, butuh approval Owner/Manager. Selisih tidak boleh ada (server menolak).</p>
            </div>
          )}
          <DialogFooter><Button className="bg-slate-900 hover:bg-slate-800" onClick={doRecon} data-testid="btn-confirm-recon">Simpan Rekonsiliasi</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
